import React, { useEffect, useState } from 'react';
import logoImg from '../assets/images/logo.png';
import { Satellite, Radio, ArrowRight, Menu, X, Mail, Phone, Eye, Clock, Crosshair } from 'lucide-react';
import { siteContent } from '../content/siteContent';

// icon keys from content -> lucide icons (used by hero callouts)
const iconMap = { eye: Eye, clock: Clock, crosshair: Crosshair };

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Force two-line headings for solution cards by splitting near the middle
  const splitHeadingIntoTwoLines = (title) => {
    if (!title || typeof title !== 'string') return [title, ''];
    const words = title.trim().split(/\s+/);
    if (words.length <= 1) return [title, ''];
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  };

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && setIsMenuOpen(false);
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  return (
    <div className="dark-container">
      {/* Header */}
      <header className="dark-header">
        <div className="dark-logo-container">
          <div className="dark-logo">
            <img src={logoImg} alt="Ulook Logo" className="logo-image" />
          </div>
        </div>

        <nav className="dark-nav">
          <a href="#technology" className="dark-nav-link">{siteContent.navigation.technology}</a>
          <a href="#solutions" className="dark-nav-link">{siteContent.navigation.solutions}</a>
          <a href="https://ulook.keka.com/careers" target="_blank" rel="noopener noreferrer" className="dark-nav-link">{siteContent.navigation.careers}</a>
          <a href="#contact" className="dark-nav-link">{siteContent.navigation.contact}</a>
          <a href="https://drive.google.com/drive/folders/1GjnEBK6GeCfQJnVBco_ZIrBzRYsUaVWa?usp=sharing" target="_blank" rel="noopener noreferrer" className="dark-nav-link">Press Kit</a>
        </nav>

        <button className="mobile-menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className={`mobile-menu ${isMenuOpen ? 'show' : ''}`}>
          <a href="#technology" className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>{siteContent.navigation.technology}</a>
          <a href="#solutions" className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>{siteContent.navigation.solutions}</a>
          <a href="https://ulook.keka.com/careers" target="_blank" rel="noopener noreferrer" className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>{siteContent.navigation.careers}</a>
          <a href="#contact" className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>{siteContent.navigation.contact}</a>
          <a href="https://drive.google.com/drive/folders/1GjnEBK6GeCfQJnVBco_ZIrBzRYsUaVWa?usp=sharing" target="_blank" rel="noopener noreferrer" className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>Press Kit</a>
        </div>
      </header>

      {/* Hero (video removed; title + description + CTA + sentence-style callouts) */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-left">
            <h1 className="hero-title">{siteContent.hero.title}</h1>
            <div className="hero-body">
              <div className="hero-content-text">
                {siteContent.hero.description.map((paragraph, index) => (
  <p key={index} className="text-lg text-gray-300 mt-4">
    {paragraph}
  </p>
))}
              </div>


            </div>

            {Array.isArray(siteContent.hero.callouts) && (
              <ul className="hero-callouts" role="list">
                {siteContent.hero.callouts.map((c, idx) => {
                  const Icon = iconMap[c.icon] || Eye;
                  return (
                    <li key={idx} className="callout" aria-label={`Callout ${idx + 1}`}>
                      <span className="callout-icon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <div className="callout-lines">
                        <div className="callout-prefix">{c.prefix}</div>
                        <div className="callout-highlight">{c.highlight}</div>
                        <div className="callout-suffix">{c.suffix}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Accent */}
        <div className="waveform-container">
          <div className="waveform-line waveform-1"></div>
          <div className="waveform-line waveform-2"></div>
          <div className="waveform-line waveform-3"></div>
        </div>
      </section>

      {/* Technology */}
      <section id="technology" className="technology-section">
        <div className="section-content">
          {/* NEW: explicit header block for clean alignment & spacing */}
          <div className="tech-header">
            <h2 className="section-title">{siteContent.technology.title}</h2>
            <p className="section-description">{siteContent.technology.description}</p>
          </div>

          <div className="tech-container">
            <div className="tech-left">
              <div className="tech-features">
                {Array.isArray(siteContent.technology?.features) && siteContent.technology.features.map((feature, index) => (
                  <div key={index} className="tech-feature">
                    <div className="feature-number">{feature.number}</div>
                    <div className="feature-content">
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="tech-right">
              <div className="tech-visual">
                <div className="radar-circle radar-1"></div>
                <div className="radar-circle radar-2"></div>
                <div className="radar-circle radar-3"></div>
                <div className="radar-center">
                  <Radio size={24} />
                </div>
                <div className="signal-dots">
                  <div className="signal-dot dot-1"></div>
                  <div className="signal-dot dot-2"></div>
                  <div className="signal-dot dot-3"></div>
                  <div className="signal-dot dot-4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="solutions-section">
        {/* Header outside section-content for full-width */}
        <div className="section-header">
          <h2 className="section-title">{siteContent.solutions.title}</h2>
          {Array.isArray(siteContent.solutions.description) ? (
            siteContent.solutions.description.map((d, i) => (
              <p key={i} className="section-description">{d}</p>
            ))
          ) : (
            <p className="section-description">{siteContent.solutions.description}</p>
          )}
        </div>

        {/* Cards grid constrained */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 w-full max-w-[2560px] mx-auto">
          {Array.isArray(siteContent.solutions?.categories) && siteContent.solutions.categories.map((cat, idx) => {
              const imgCount = Math.min(Array.isArray(cat.images) ? cat.images.length : 0, 3);
              const colsClass = imgCount === 3 ? 'cols-3' : imgCount === 2 ? 'cols-2' : '';
              const [line1, line2] = splitHeadingIntoTwoLines(cat.title);
              return (
                <div key={idx} className="solution-card">
                  <h3 className="solution-title">
                    <span className="block">{line1}</span>
                    {line2 && <span className="block">{line2}</span>}
                  </h3>
                  {cat.description && (
                    <p className="solution-description">{cat.description}</p>
                  )}
                  {Array.isArray(cat.images) && cat.images.length > 0 && (
                    <div className={`solution-images ${colsClass}`.trim()}>
                      {cat.images.map((src, i) => (
                        <div key={i} className="solution-image">
                          <img src={src} alt={`${cat.title} ${i + 1}`} loading="lazy" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="cta-section">
        <div className="section-content">
          <div className="cta-container">
            <div className="cta-content">
              <h2 className="cta-title">{siteContent.cta.title}</h2>
              <p className="cta-description">{siteContent.cta.description}</p>

              <div className="cta-buttons">
                <a
                  href={`mailto:${siteContent.contact.email}?subject=Contact ${siteContent.company.name}`}
                  className="btn-primary"
                >
                  <span>{siteContent.cta.buttons.primary}</span>
                  <ArrowRight size={20} />
                </a>
              </div>
            </div>

            <div className="cta-visual">
              <div className="pulse-ring ring-1"></div>
              <div className="pulse-ring ring-2"></div>
              <div className="pulse-ring ring-3"></div>
              <div className="cta-icon">
                <Satellite size={48} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-logo-combo">
              <div className="footer-translation">उलूक / ULŪK / NOUN</div>
            </div>
            <p className="footer-description">{siteContent.footer.description}</p>
            <div className="footer-legal">
              {siteContent.footer.contactInfo.companyDetails.map((line, idx) => (
                <p key={idx} className="footer-legal-line">{line}</p>
              ))}
              <a href={siteContent.footer.annualReturns.href} className="footer-annual-link" target="_blank" rel="noreferrer">
                {siteContent.footer.annualReturns.text}
              </a>
              <div className="footer-grievance">
                <h5>Grievance Officer</h5>
                <p className="footer-grievance-subtitle">{siteContent.footer.grievance.subtitle}</p>
                <a href={`mailto:${siteContent.footer.grievance.email}`} className="footer-link-button">
                  <Mail size={16} />
                  {siteContent.footer.grievance.email}
                </a>
                <div className="footer-grievance-address">
                  {siteContent.footer.grievance.address.map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="footer-right">
            <div className="footer-links">
              <div className="footer-column">
                <h4>Company</h4>
                {siteContent.footer.links.company.map((link, index) => (
                  <a key={index} href={link.href}>{link.text}</a>
                ))}
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <a href={`mailto:${siteContent.footer.contactInfo.email}`} className="footer-link-button">
                  <Mail size={16} />
                  {siteContent.footer.contactInfo.email}
                </a>
                <a href={`tel:${siteContent.footer.contactInfo.phone}`} className="footer-link-button">
                  <Phone size={16} />
                  {siteContent.footer.contactInfo.phone}
                </a>
              </div>
              <div className="footer-column">
                <h4>Office</h4>
                <div className="footer-address">
                  {siteContent.footer.contactInfo.address.map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {siteContent.footer.copyright}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
